import { useEffect, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useApp } from '@/components/AppLayout';
import {
  reportsApi,
  clientApi,
  type InvoiceHistoryReport,
  type InvoiceHistoryRow,
  type ReportExportFormat,
  type Client,
} from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import {
  ReportLayout,
  SummaryCard,
  SummaryCardsGrid,
  ExportDropdown,
  TimeRangePicker,
  createTimeRange,
  type TimeRange,
} from '@/components/reports';
import type { ColumnDef } from '@tanstack/react-table';
import { FileText, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { INVOICE_STATUS_CONFIG, type InvoiceStatus } from '@/lib/invoiceStatus';

export default function InvoiceHistory() {
  const { currentBusiness } = useApp();
  const [report, setReport] = useState<InvoiceHistoryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>(() => createTimeRange('this_year'));
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState(1);

  // Load clients for filter dropdown
  useEffect(() => {
    if (currentBusiness) {
      clientApi.list(currentBusiness.id).then((response) => {
        if (response.data) {
          setClients(response.data);
        }
      });
    }
  }, [currentBusiness]);

  useEffect(() => {
    if (currentBusiness) {
      loadReport();
    }
  }, [currentBusiness, timeRange, statusFilter, clientFilter, searchTerm, page]);

  const loadReport = async () => {
    if (!currentBusiness) return;
    setLoading(true);
    try {
      const response = await reportsApi.getInvoiceHistory(
        currentBusiness.id,
        timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
        timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
        timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined,
        statusFilter || undefined,
        clientFilter || undefined,
        searchTerm || undefined,
        page,
        50
      );
      if (response.data) {
        setReport(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportFormat: ReportExportFormat) => {
    if (!currentBusiness) return;
    await reportsApi.exportInvoiceHistory(
      currentBusiness.id,
      exportFormat,
      timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
      timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
      timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined,
      statusFilter || undefined,
      clientFilter || undefined,
      searchTerm || undefined
    );
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadReport();
  };

  const columns: ColumnDef<InvoiceHistoryRow>[] = useMemo(() => [
    {
      accessorKey: 'invoice_number',
      header: ({ column }) => <SortableHeader column={column} title="Invoice #" />,
    },
    {
      accessorKey: 'client_name',
      header: ({ column }) => <SortableHeader column={column} title="Client" />,
    },
    {
      accessorKey: 'invoice_date',
      header: ({ column }) => <SortableHeader column={column} title="Date" />,
      cell: ({ row }) => {
        const dateStr = row.getValue('invoice_date') as string | null;
        return dateStr ? format(new Date(dateStr), 'MMM d, yyyy') : '-';
      },
    },
    {
      accessorKey: 'due_date',
      header: 'Due Date',
      cell: ({ row }) => {
        const dateStr = row.getValue('due_date') as string | null;
        return dateStr ? format(new Date(dateStr), 'MMM d, yyyy') : '-';
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;
        const config = INVOICE_STATUS_CONFIG[status as InvoiceStatus];
        return (
          <span className={config?.textColorClass || 'text-muted-foreground'}>
            {row.original.status_display}
          </span>
        );
      },
    },
    {
      accessorKey: 'total_amount',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Total" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right font-medium">
          {formatAmount(row.getValue('total_amount'))}
        </div>
      ),
    },
    {
      accessorKey: 'amount_paid',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Paid" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-green-600">
          {formatAmount(row.getValue('amount_paid'))}
        </div>
      ),
    },
    {
      accessorKey: 'balance_due',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Balance" />
        </div>
      ),
      cell: ({ row }) => {
        const value = row.getValue('balance_due') as string;
        const isNonZero = parseFloat(value) > 0;
        return (
          <div className={`text-right ${isNonZero ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
            {formatAmount(value)}
          </div>
        );
      },
    },
  ], []);

  const pagination = report?.summary.pagination;

  return (
    <ReportLayout
      title="Invoice History"
      actions={<ExportDropdown onExport={handleExport} disabled={loading || !report} />}
      filters={
        <div className="flex flex-wrap items-center gap-4">
          <TimeRangePicker
            value={timeRange}
            onChange={(newRange) => {
              setTimeRange(newRange);
              setPage(1);
            }}
          />
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value === 'all' ? '' : value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="bad_debt">Bad Debt</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={clientFilter}
            onValueChange={(value) => {
              setClientFilter(value === 'all' ? '' : value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.billing_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <form onSubmit={handleSearchSubmit} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-[200px]"
            />
          </form>
        </div>
      }
    >
      {report && (
        <div className="mb-6">
          <SummaryCardsGrid>
            <SummaryCard
              label="Total Invoices"
              value={report.summary.total_count.toString()}
            />
            <SummaryCard
              label="Total Amount"
              value={formatAmount(report.summary.total_amount)}
            />
            <SummaryCard
              label="Total Paid"
              value={formatAmount(report.summary.total_paid)}
              variant="success"
            />
            <SummaryCard
              label="Balance Due"
              value={formatAmount(report.summary.total_balance)}
              variant={parseFloat(report.summary.total_balance) > 0 ? 'warning' : 'default'}
            />
          </SummaryCardsGrid>
        </div>
      )}
      <DataTable
        columns={columns}
        data={report?.data ?? []}
        loading={loading}
        emptyIcon={<FileText className="h-6 w-6 text-muted-foreground" />}
        emptyTitle="No Invoices Found"
        emptyDescription="No invoices match your current filters."
      />
      {!loading && pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.total_pages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={!pagination.has_prev}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={!pagination.has_next}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </ReportLayout>
  );
}
